(() => {
  const ELEMENT_NAME = 'pfm-fiber-carousel';
  const LOOP_BUFFER_CYCLES = 2;
  const SCROLL_SETTLE_DELAY = 260;
  const MOMENTUM_SAMPLE_WINDOW = 120;
  const MOMENTUM_RELEASE_STALE_DELAY = 100;
  const MOMENTUM_MIN_VELOCITY = 0.12;
  const MOMENTUM_MAX_VELOCITY = 2.4;
  const MOMENTUM_STOP_VELOCITY = 0.025;
  const MOMENTUM_TIME_CONSTANT = 220;
  const MOMENTUM_MAX_DURATION = 1000;

  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  class PrestigeFiberCarousel extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-initialized')) {
        return;
      }

      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.items = Array.from(this.querySelectorAll('[data-carousel-item]'));
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.navigation = this.querySelector('[data-carousel-navigation]');
      this.currentCounter = this.querySelector('[data-carousel-current]');
      this.totalCounter = this.querySelector('[data-carousel-total]');
      this.counter = this.currentCounter?.closest('.pfm-fiber-carousel__counter');
      this.autoplayToggle = this.querySelector('[data-carousel-autoplay-toggle]');
      this.pauseIcon = this.querySelector('[data-carousel-pause-icon]');
      this.playIcon = this.querySelector('[data-carousel-play-icon]');

      if (!this.viewport || !this.track || this.items.length === 0) {
        return;
      }

      this.setAttribute('data-initialized', '');
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverCapableQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.loopRequested = this.dataset.loop === 'true' && this.items.length > 1;
      this.loopEnabled = this.loopRequested;
      this.loopActive = false;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.items.length > 1;
      this.autoplayInterval = Math.max(3000, Number.parseInt(this.dataset.autoplayInterval, 10) || 5000);
      this.autoplayPausedByUser = false;
      this.isIntersecting = typeof IntersectionObserver === 'undefined';
      this.isHovering = false;
      this.isPointerActive = false;
      this.isMomentumActive = false;
      this.isScrolling = false;
      this.isRecentering = false;
      this.hasFocusWithin = false;
      this.activePointerId = null;
      this.pointerState = null;
      this.suppressClick = false;
      this.pendingPhysicalIndex = null;
      this.activeLogicalIndex = 0;
      this.pendingResize = false;
      this.cloneItems = [];
      this.renderedItems = [];
      this.renderedPositions = [];
      this.originalContentSpan = 0;
      this.cycleSpan = 0;
      this.counterTimer = null;
      this.clickReleaseTimer = null;
      this.autoplayTimer = null;
      this.focusTimer = null;
      this.scrollSettleTimer = null;
      this.scrollRaf = null;
      this.momentumRaf = null;
      this.momentumState = null;
      this.momentumRecenterRaf = null;
      this.resizeRaf = null;
      this.recenterRafOne = null;
      this.recenterRafTwo = null;
      this.recenterGeneration = 0;
      this.lastViewportWidth = this.viewport.clientWidth;

      this.prepareOriginalItems();
      this.configureLoop({ logicalIndex: 0, initial: true });

      this.viewport.querySelectorAll('.pfm-fiber-card__link, .pfm-fiber-card__image').forEach((element) => {
        element.draggable = false;
      });

      const options = { signal: this.abortController.signal };

      this.previousButton?.addEventListener('click', () => this.handleManualMove(-1), options);
      this.nextButton?.addEventListener('click', () => this.handleManualMove(1), options);
      this.autoplayToggle?.addEventListener('click', () => this.toggleAutoplay(), options);
      this.viewport.addEventListener('scroll', () => this.handleScroll(), { ...options, passive: true });
      this.viewport.addEventListener('scrollend', () => this.handleScrollEnd(), options);
      this.viewport.addEventListener('keydown', (event) => this.handleKeydown(event), options);
      this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event), options);
      this.viewport.addEventListener('pointermove', (event) => this.handlePointerMove(event), options);
      this.viewport.addEventListener(
        'wheel',
        () => this.stopMomentum({ settle: false }),
        { ...options, passive: true },
      );
      this.viewport.addEventListener('lostpointercapture', (event) => this.finishPointerGesture(event), options);
      window.addEventListener('pointerup', (event) => this.finishPointerGesture(event), options);
      window.addEventListener('pointercancel', (event) => this.finishPointerGesture(event), options);
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), {
        ...options,
        capture: true,
      });
      this.viewport.addEventListener(
        'mousedown',
        (event) => {
          if (
            event.button === 0 &&
            event.target.closest('[data-carousel-clone] a, [data-carousel-clone] [data-pfm-quick-add]')
          ) {
            event.preventDefault();
          }
        },
        options,
      );
      this.viewport.addEventListener(
        'click',
        (event) => this.handleViewportActivation(event),
        { ...options, capture: true },
      );
      this.viewport.addEventListener(
        'auxclick',
        (event) => this.handleViewportActivation(event),
        { ...options, capture: true },
      );

      this.addEventListener(
        'mouseenter',
        () => {
          if (!this.hoverCapableQuery.matches) {
            return;
          }

          this.isHovering = true;
          this.stopAutoplay();
          this.updateAutoplayControl();
        },
        options,
      );
      this.addEventListener(
        'mouseleave',
        () => {
          if (!this.hoverCapableQuery.matches) {
            return;
          }

          this.isHovering = false;
          this.scheduleAutoplay();
        },
        options,
      );
      this.addEventListener(
        'focusin',
        (event) => {
          if (event.target.matches?.(':focus-visible')) {
            this.hasFocusWithin = true;
            this.stopAutoplay();
            this.updateAutoplayControl();
          }
        },
        options,
      );
      this.addEventListener(
        'focusout',
        () => {
          window.clearTimeout(this.focusTimer);
          this.focusTimer = window.setTimeout(() => {
            const activeElement = document.activeElement;
            this.hasFocusWithin = Boolean(
              this.contains(activeElement) &&
              activeElement?.matches?.(':focus-visible'),
            );
            this.scheduleAutoplay();
          }, 0);
        },
        options,
      );
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.stopMomentum({ settle: true });
          }

          this.scheduleAutoplay();
        },
        options,
      );

      if (window.Shopify?.designMode) {
        document.addEventListener(
          'shopify:block:select',
          (event) => {
            const item = event.target.closest?.('[data-carousel-item]');

            if (item && this.contains(item)) {
              this.scrollToIndex(this.items.indexOf(item), { instant: true });
            }
          },
          options,
        );
      }

      this.handleReducedMotionChange = () => {
        if (this.reducedMotionQuery.matches) {
          this.stopMomentum({ settle: true });
        }

        this.scheduleAutoplay();
      };
      this.reducedMotionQuery.addEventListener?.('change', this.handleReducedMotionChange);

      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.viewport);

      if (typeof IntersectionObserver !== 'undefined') {
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isIntersecting = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.2);
            this.scheduleAutoplay();
          },
          { threshold: [0, 0.2, 0.5] },
        );
        this.intersectionObserver.observe(this);
      }

      if (this.totalCounter) {
        this.totalCounter.textContent = String(this.items.length);
      }

      requestAnimationFrame(() => {
        this.updateState();
        this.scheduleAutoplay();
      });
    }

    disconnectedCallback() {
      this.stopMomentum({ settle: false });
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.reducedMotionQuery?.removeEventListener?.('change', this.handleReducedMotionChange);
      window.clearTimeout(this.counterTimer);
      window.clearTimeout(this.clickReleaseTimer);
      window.clearTimeout(this.autoplayTimer);
      window.clearTimeout(this.focusTimer);
      window.clearTimeout(this.scrollSettleTimer);
      window.cancelAnimationFrame(this.scrollRaf);
      window.cancelAnimationFrame(this.momentumRecenterRaf);
      window.cancelAnimationFrame(this.resizeRaf);
      window.cancelAnimationFrame(this.recenterRafOne);
      window.cancelAnimationFrame(this.recenterRafTwo);

      this.cloneItems?.forEach((clone) => clone.remove());
      this.items?.forEach((item) => {
        delete item.dataset.carouselRenderedItem;
        delete item.dataset.carouselLogicalIndex;
        delete item.dataset.carouselCycle;
      });

      if (this.viewport) {
        this.viewport.scrollLeft = 0;
        delete this.viewport.dataset.carouselRecentering;
      }

      delete this.dataset.seamlessLoop;
      this.removeAttribute('data-initialized');
    }

    prepareOriginalItems() {
      this.items.forEach((item, index) => {
        item.dataset.carouselRenderedItem = '';
        item.dataset.carouselLogicalIndex = String(index);
        item.dataset.carouselCycle = '0';
      });

      this.renderedItems = [...this.items];
      this.refreshRenderedPositions();
      this.originalContentSpan = this.measureOriginalContentSpan();
    }

    configureLoop({ logicalIndex = this.currentIndex, initial = false } = {}) {
      const safeLogicalIndex = Math.max(0, Math.min(this.items.length - 1, logicalIndex || 0));
      this.originalContentSpan = this.measureOriginalContentSpan();
      const originalsOverflow = this.originalContentSpan > this.viewport.clientWidth + 2;
      const shouldBuildLoop =
        this.loopRequested &&
        !window.Shopify?.designMode &&
        this.items.length > 1 &&
        originalsOverflow;

      if (shouldBuildLoop && !this.loopActive) {
        this.buildLoopClones();
      } else if (!shouldBuildLoop && this.loopActive) {
        this.destroyLoopClones();
      }

      this.refreshRenderedPositions();
      this.originalContentSpan = this.measureOriginalContentSpan();

      if (this.loopActive) {
        const firstSuffix = this.renderedItems.find(
          (item) => item.dataset.carouselCycle === '1' && item.dataset.carouselLogicalIndex === '0',
        );
        const originalStart = this.getItemStart(this.items[0]);
        const suffixStart = firstSuffix ? this.getItemStart(firstSuffix) : originalStart;
        this.cycleSpan = suffixStart - originalStart;

        if (this.cycleSpan <= 2) {
          this.destroyLoopClones();
          this.refreshRenderedPositions();
        }
      }

      const target = this.getItemStart(this.items[safeLogicalIndex]);

      if (initial) {
        this.viewport.scrollLeft = target;
      } else {
        this.applyInstantScroll(target, () => this.finishSettledState());
      }

      this.updateState();
    }

    buildLoopClones() {
      this.cloneItems.forEach((clone) => clone.remove());
      this.cloneItems = [];

      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      for (let cycle = -LOOP_BUFFER_CYCLES; cycle < 0; cycle += 1) {
        this.items.forEach((item, logicalIndex) => {
          const clone = this.createLoopClone(item, logicalIndex, cycle);
          this.cloneItems.push(clone);
          beforeFragment.append(clone);
        });
      }

      for (let cycle = 1; cycle <= LOOP_BUFFER_CYCLES; cycle += 1) {
        this.items.forEach((item, logicalIndex) => {
          const clone = this.createLoopClone(item, logicalIndex, cycle);
          this.cloneItems.push(clone);
          afterFragment.append(clone);
        });
      }

      this.track.insertBefore(beforeFragment, this.items[0]);
      this.track.append(afterFragment);
      this.loopActive = true;
      this.dataset.seamlessLoop = 'true';
      this.renderedItems = Array.from(this.track.querySelectorAll('[data-carousel-rendered-item]'));
    }

    createLoopClone(item, logicalIndex, cycle) {
      const clone = item.cloneNode(true);

      // Loop copies are visual only. Never connect cloned native product forms,
      // purchase listeners or dialogs; clicks forward to the canonical action.
      clone.querySelectorAll('quick-buy-modal').forEach((element) => element.remove());
      clone.querySelectorAll('product-form, buy-buttons, form').forEach((element) => {
        const visualWrapper = document.createElement('div');
        visualWrapper.className = element.className;
        visualWrapper.append(...element.childNodes);
        element.replaceWith(visualWrapper);
      });
      const cloneTree = [clone, ...clone.querySelectorAll('*')];

      cloneTree.forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (
            attribute.name === 'id' ||
            attribute.name === 'for' ||
            attribute.name === 'role' ||
            attribute.name.startsWith('aria-') ||
            attribute.name.startsWith('data-shopify-editor-') ||
            attribute.name === 'data-product-id' ||
            attribute.name === 'data-variant-id'
          ) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      clone.removeAttribute('data-carousel-item');
      clone.dataset.carouselClone = '';
      clone.dataset.carouselRenderedItem = '';
      clone.dataset.carouselLogicalIndex = String(logicalIndex);
      clone.dataset.carouselCycle = String(cycle);
      clone.setAttribute('aria-hidden', 'true');

      clone.querySelectorAll('a').forEach((element) => {
        element.removeAttribute('href');
        element.removeAttribute('target');
        element.removeAttribute('download');
        element.removeAttribute('ping');
        element.removeAttribute('referrerpolicy');
        element.removeAttribute('tabindex');
      });

      clone.querySelectorAll('button, input, select, textarea').forEach((element) => {
        // The visual quick-add remains pointer-clickable, never keyboard-focusable.
        // It cannot submit a form: all clone form semantics have been removed.
        if (element.matches('button[data-pfm-quick-add]') && !element.disabled) {
          element.type = 'button';
        } else {
          element.disabled = true;
        }
        element.setAttribute('tabindex', '-1');
      });

      clone.querySelectorAll('[contenteditable], [tabindex]').forEach((element) => {
        element.removeAttribute('contenteditable');
        if (!element.matches('button[data-pfm-quick-add]')) {
          element.removeAttribute('tabindex');
        }
      });

      return clone;
    }

    destroyLoopClones() {
      this.cloneItems.forEach((clone) => clone.remove());
      this.cloneItems = [];
      this.renderedItems = [...this.items];
      this.loopActive = false;
      this.cycleSpan = 0;
      delete this.dataset.seamlessLoop;
    }

    refreshRenderedPositions() {
      if (this.loopActive) {
        this.renderedItems = Array.from(this.track.querySelectorAll('[data-carousel-rendered-item]'));
      } else {
        this.renderedItems = [...this.items];
      }

      const trackStart = this.track.getBoundingClientRect().left;
      this.renderedPositions = this.renderedItems.map(
        (item) => item.getBoundingClientRect().left - trackStart,
      );
    }

    measureOriginalContentSpan() {
      const firstItem = this.items[0];
      const lastItem = this.items[this.items.length - 1];

      if (!firstItem || !lastItem) {
        return 0;
      }

      const firstRect = firstItem.getBoundingClientRect();
      const lastRect = lastItem.getBoundingClientRect();

      return lastRect.right - firstRect.left;
    }

    getItemStart(item) {
      if (!item) {
        return 0;
      }

      return item.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    }

    get nearestRenderedItem() {
      if (this.renderedPositions.length !== this.renderedItems.length) {
        this.refreshRenderedPositions();
      }

      const viewportStart = this.viewport.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.renderedPositions.forEach((position, index) => {
        const distance = Math.abs(position - viewportStart);

        if (distance < closestDistance) {
          closestIndex = index;
          closestDistance = distance;
        }
      });

      const item = this.renderedItems[closestIndex];

      return {
        item,
        physicalIndex: closestIndex,
        logicalIndex: Number.parseInt(item?.dataset.carouselLogicalIndex || '0', 10),
        cycle: Number.parseInt(item?.dataset.carouselCycle || '0', 10),
      };
    }

    get currentIndex() {
      if (this.loopActive) {
        return this.nearestRenderedItem.logicalIndex;
      }

      const { atStart, atEnd } = this.scrollBoundaries;

      if (atStart) {
        return 0;
      }

      if (atEnd) {
        return this.items.length - 1;
      }

      return this.nearestRenderedItem.logicalIndex;
    }

    get scrollBoundaries() {
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);

      return {
        maxScroll,
        atStart: this.viewport.scrollLeft <= 2,
        atEnd: this.viewport.scrollLeft >= maxScroll - 2,
      };
    }

    get hasScrollableContent() {
      if (this.loopActive) {
        return this.originalContentSpan > this.viewport.clientWidth + 2;
      }

      return this.scrollBoundaries.maxScroll > 2;
    }

    get canAutoplay() {
      if (
        !this.autoplayEnabled ||
        !this.hasScrollableContent ||
        this.autoplayPausedByUser ||
        this.reducedMotionQuery.matches ||
        window.Shopify?.designMode ||
        document.hidden ||
        !this.isIntersecting ||
        this.isHovering ||
        this.isPointerActive ||
        this.isMomentumActive ||
        this.isScrolling ||
        this.isRecentering ||
        this.hasFocusWithin
      ) {
        return false;
      }

      return this.loopActive || !this.scrollBoundaries.atEnd;
    }

    handleManualMove(direction) {
      this.stopMomentum({ settle: false });
      this.stopAutoplay();
      const moved = this.moveBy(direction);

      if (moved) {
        this.queueScrollSettled();
      } else {
        this.finishSettledState();
      }
    }

    moveBy(direction, { automatic = false } = {}) {
      if (!this.hasScrollableContent) {
        return false;
      }

      let instant = false;
      let destination;

      if (this.loopActive) {
        const nearestIndex = this.nearestRenderedItem.physicalIndex;

        if (
          this.pendingPhysicalIndex !== null &&
          Math.abs(this.pendingPhysicalIndex - nearestIndex) >= this.items.length
        ) {
          return true;
        }

        let baseIndex = this.pendingPhysicalIndex ?? nearestIndex;
        destination = baseIndex + direction;

        const innerStart = this.items.length;
        const innerEnd = this.renderedItems.length - this.items.length - 1;

        if (destination < innerStart || destination > innerEnd) {
          const itemShift = destination < innerStart ? this.items.length : -this.items.length;
          baseIndex += itemShift;
          destination += itemShift;
          this.shiftLoopPosition(itemShift / this.items.length);
        }

        destination = Math.max(0, Math.min(this.renderedItems.length - 1, destination));
      } else {
        const { atStart, atEnd } = this.scrollBoundaries;
        destination = this.currentIndex + direction;

        if (this.loopRequested && direction > 0 && atEnd) {
          destination = 0;
          instant = true;
        } else if (this.loopRequested && direction < 0 && atStart) {
          destination = this.items.length - 1;
          instant = true;
        } else {
          destination = Math.max(0, Math.min(this.items.length - 1, destination));
        }
      }

      this.pendingPhysicalIndex = destination;
      this.isScrolling = true;
      this.scrollToRenderedIndex(destination, { instant });

      if (!automatic) {
        this.updateAutoplayControl();
      }

      return true;
    }

    scrollToRenderedIndex(index, { instant = false } = {}) {
      const item = this.renderedItems[index];

      if (!item) {
        return;
      }

      this.viewport.scrollTo({
        left: this.renderedPositions[index] ?? this.getItemStart(item),
        behavior: instant || this.reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }

    scrollToIndex(index, { instant = false } = {}) {
      const safeIndex = Math.max(0, Math.min(this.items.length - 1, index));
      const renderedIndex = this.renderedItems.indexOf(this.items[safeIndex]);

      if (renderedIndex < 0) {
        return;
      }

      this.stopMomentum({ settle: false });
      this.stopAutoplay();
      this.pendingPhysicalIndex = renderedIndex;
      this.isScrolling = true;
      this.scrollToRenderedIndex(renderedIndex, { instant });
      this.queueScrollSettled();
    }

    applyInstantScroll(left, onComplete) {
      const generation = ++this.recenterGeneration;
      window.cancelAnimationFrame(this.recenterRafOne);
      window.cancelAnimationFrame(this.recenterRafTwo);
      this.isRecentering = true;
      this.viewport.dataset.carouselRecentering = 'true';
      this.viewport.scrollLeft = left;

      this.recenterRafOne = window.requestAnimationFrame(() => {
        this.recenterRafTwo = window.requestAnimationFrame(() => {
          if (generation !== this.recenterGeneration) {
            return;
          }

          delete this.viewport.dataset.carouselRecentering;
          this.isRecentering = false;
          onComplete?.();
        });
      });
    }

    normalizeLoopPosition(onComplete) {
      if (!this.loopActive || this.cycleSpan <= 2) {
        return false;
      }

      const { cycle } = this.nearestRenderedItem;

      if (cycle === 0) {
        return false;
      }

      const delta = -cycle * this.cycleSpan;
      const target = this.viewport.scrollLeft + delta;

      if (this.pointerState) {
        this.pointerState.startScrollLeft += delta;
      }

      this.applyInstantScroll(target, onComplete);
      return true;
    }

    shiftLoopPosition(cycleShift) {
      if (!this.loopActive || !cycleShift || this.cycleSpan <= 2) {
        return;
      }

      const delta = cycleShift * this.cycleSpan;

      if (this.pointerState) {
        this.pointerState.startScrollLeft += delta;
      }

      this.applyInstantScroll(this.viewport.scrollLeft + delta);
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay() {
      this.stopAutoplay();
      this.updateAutoplayControl();

      if (!this.canAutoplay) {
        return;
      }

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        const moved = this.moveBy(1, { automatic: true });

        if (moved) {
          this.queueScrollSettled();
        } else {
          this.finishSettledState();
        }
      }, this.autoplayInterval);
    }

    toggleAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotionQuery.matches) {
        return;
      }

      this.stopMomentum({ settle: true });

      if (this.autoplayPausedByUser || this.hasFocusWithin) {
        this.autoplayPausedByUser = false;
        this.hasFocusWithin = false;
      } else {
        this.autoplayPausedByUser = true;
      }

      this.scheduleAutoplay();
    }

    updateAutoplayControl() {
      const reducedMotion = this.reducedMotionQuery.matches;
      const stopped = this.autoplayPausedByUser || this.hasFocusWithin;
      const designMode = Boolean(window.Shopify?.designMode);

      if (this.autoplayToggle) {
        this.autoplayToggle.hidden = !this.autoplayEnabled || !this.hasScrollableContent || reducedMotion || designMode;
        this.autoplayToggle.setAttribute(
          'aria-label',
          stopped ? 'Start automatic product rotation' : 'Pause automatic product rotation',
        );
      }

      if (this.pauseIcon) {
        this.pauseIcon.hidden = stopped;
      }

      if (this.playIcon) {
        this.playIcon.hidden = !stopped;
      }

      if (this.counter) {
        this.counter.setAttribute('aria-live', this.canAutoplay ? 'off' : 'polite');
      }
    }

    handleScroll() {
      if (this.isRecentering) {
        return;
      }

      this.isScrolling = true;
      this.stopAutoplay();
      this.updateControls();

      if (!this.scrollRaf) {
        this.scrollRaf = window.requestAnimationFrame(() => {
          this.scrollRaf = null;
          this.updateCounter();
        });
      }

      if (this.loopActive) {
        const { cycle } = this.nearestRenderedItem;

        if (Math.abs(cycle) >= LOOP_BUFFER_CYCLES && this.pointerState?.dragging) {
          this.pendingPhysicalIndex = null;
          this.normalizeLoopPosition(() => this.queueScrollSettled());
          return;
        }
      }

      this.queueScrollSettled();
    }

    handleScrollEnd() {
      if (!this.isPointerActive && !this.isMomentumActive && !this.isRecentering) {
        window.clearTimeout(this.scrollSettleTimer);
        this.handleScrollSettled();
      }
    }

    queueScrollSettled(delay = SCROLL_SETTLE_DELAY) {
      window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = window.setTimeout(() => this.handleScrollSettled(), delay);
    }

    handleScrollSettled() {
      if (this.isPointerActive || this.isMomentumActive || this.isRecentering) {
        this.queueScrollSettled();
        return;
      }

      this.isScrolling = false;
      this.pendingPhysicalIndex = null;

      if (this.normalizeLoopPosition(() => this.finishSettledState())) {
        return;
      }

      this.finishSettledState();
    }

    finishSettledState() {
      this.isScrolling = false;
      this.pendingPhysicalIndex = null;
      this.updateControls();
      this.updateCounter({ commit: true });
      this.updateAutoplayControl();

      if (this.pendingResize) {
        this.pendingResize = false;
        this.handleResize(true);
        return;
      }

      this.scheduleAutoplay();
    }

    handleKeydown(event) {
      if (event.target !== this.viewport) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handleManualMove(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleManualMove(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.scrollToIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.scrollToIndex(this.items.length - 1);
      }
    }

    handleViewportActivation(event) {
      const clone = event.target.closest?.('[data-carousel-clone]');

      if (!clone) {
        if (this.suppressClick) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }

        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        this.suppressClick ||
        this.isPointerActive ||
        this.isScrolling ||
        this.isRecentering ||
        (event.type === 'auxclick' && event.button !== 1)
      ) {
        return;
      }

      const logicalIndex = Number.parseInt(clone.dataset.carouselLogicalIndex || '-1', 10);
      const original = this.items[logicalIndex];
      const quickAddTarget = event.target.closest?.('[data-pfm-quick-add]');

      if (quickAddTarget) {
        if (event.type !== 'click' || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
          return;
        }

        const canonicalAction = original?.querySelector('[data-pfm-quick-add]');
        if (canonicalAction && !canonicalAction.disabled && canonicalAction.getAttribute('aria-busy') !== 'true') {
          canonicalAction.click();
        }
        return;
      }

      const linkTarget = event.target.closest?.('[data-pfm-card-link]');
      const canonicalLink = original?.querySelector('.pfm-fiber-card__link--quick-add')
        ? (linkTarget ? Array.from(original.querySelectorAll('[data-pfm-card-link]')).find((link) => link.dataset.pfmCardLink === linkTarget.dataset.pfmCardLink) : null)
        : original?.querySelector('.pfm-fiber-card__link');

      if (!canonicalLink?.href) {
        return;
      }

      if (event.type === 'auxclick' || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        window.open(canonicalLink.href, '_blank', 'noopener');
        return;
      }

      canonicalLink.click();
    }

    handlePointerDown(event) {
      if (event.isPrimary !== false) {
        this.stopMomentum({ settle: false });
        this.activePointerId = event.pointerId;
        this.isPointerActive = true;
        this.pendingPhysicalIndex = null;
        this.stopAutoplay();
        this.updateAutoplayControl();
      }

      const supportsCustomDrag = event.pointerType === 'mouse' || event.pointerType === 'pen';

      if (
        !supportsCustomDrag ||
        event.isPrimary === false ||
        event.button !== 0 ||
        event.target.closest('button, input, select, textarea')
      ) {
        return;
      }

      this.pointerState = {
        id: event.pointerId,
        type: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: this.viewport.scrollLeft,
        dragging: false,
        samples: [{ x: event.clientX, time: performance.now() }],
        lastMoveTime: 0,
      };
    }

    handlePointerMove(event) {
      if (!this.pointerState || this.pointerState.id !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - this.pointerState.startX;
      const deltaY = event.clientY - this.pointerState.startY;

      if (!this.pointerState.dragging) {
        const horizontalDistance = Math.abs(deltaX);
        const verticalDistance = Math.abs(deltaY);

        if (horizontalDistance < 7 || horizontalDistance <= verticalDistance) {
          return;
        }

        this.pointerState.dragging = true;
        this.viewport.dataset.dragging = 'true';

        try {
          this.viewport.setPointerCapture?.(event.pointerId);
        } catch (error) {
          // The pointer may have ended between the threshold check and capture.
        }
      }

      event.preventDefault();
      this.viewport.scrollLeft = this.pointerState.startScrollLeft - deltaX;
      this.recordPointerSample(event.clientX);
    }

    finishPointerGesture(event) {
      if (this.activePointerId !== event.pointerId) {
        return;
      }

      this.activePointerId = null;
      this.isPointerActive = false;

      if (!this.pointerState || this.pointerState.id !== event.pointerId) {
        this.queueScrollSettled();
        return;
      }

      const pointerState = this.pointerState;
      const wasDragging = pointerState.dragging;
      const releaseVelocity = event.type === 'pointercancel'
        ? 0
        : this.getReleaseVelocity(pointerState);
      this.pointerState = null;

      if (event.type !== 'lostpointercapture' && this.viewport.hasPointerCapture?.(event.pointerId)) {
        try {
          this.viewport.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture can disappear when the browser cancels a gesture.
        }
      }

      delete this.viewport.dataset.dragging;

      if (wasDragging) {
        this.suppressClick = true;
        window.clearTimeout(this.clickReleaseTimer);

        this.clickReleaseTimer = window.setTimeout(() => {
          this.suppressClick = false;
        }, 180);
      }

      if (wasDragging && this.startMomentum(releaseVelocity)) {
        return;
      }

      this.queueScrollSettled();
    }

    recordPointerSample(clientX) {
      if (!this.pointerState) {
        return;
      }

      const now = performance.now();
      this.pointerState.lastMoveTime = now;
      this.pointerState.samples.push({ x: clientX, time: now });

      const cutoff = now - MOMENTUM_SAMPLE_WINDOW;

      while (
        this.pointerState.samples.length > 2 &&
        this.pointerState.samples[1].time < cutoff
      ) {
        this.pointerState.samples.shift();
      }
    }

    getReleaseVelocity(pointerState) {
      const now = performance.now();

      if (
        !pointerState.lastMoveTime ||
        now - pointerState.lastMoveTime > MOMENTUM_RELEASE_STALE_DELAY
      ) {
        return 0;
      }

      const samples = pointerState.samples.filter(
        (sample) => sample.time >= now - MOMENTUM_SAMPLE_WINDOW,
      );
      const firstSample = samples[0];
      const lastSample = samples[samples.length - 1];
      const elapsed = lastSample?.time - firstSample?.time;

      if (!firstSample || !lastSample || elapsed < 12) {
        return 0;
      }

      const velocity = -(lastSample.x - firstSample.x) / elapsed;

      return Math.max(-MOMENTUM_MAX_VELOCITY, Math.min(MOMENTUM_MAX_VELOCITY, velocity));
    }

    startMomentum(velocity) {
      if (
        this.reducedMotionQuery.matches ||
        window.Shopify?.designMode ||
        !this.hasScrollableContent ||
        Math.abs(velocity) < MOMENTUM_MIN_VELOCITY
      ) {
        return false;
      }

      this.stopMomentum({ settle: false });
      this.isMomentumActive = true;
      this.isScrolling = true;
      this.momentumState = {
        velocity,
        elapsed: 0,
        lastTime: null,
      };
      this.viewport.dataset.momentum = 'true';
      this.stopAutoplay();
      this.updateAutoplayControl();

      const step = (timestamp) => {
        const state = this.momentumState;

        if (!this.isMomentumActive || !state) {
          return;
        }

        if (state.lastTime === null) {
          state.lastTime = timestamp;
          this.momentumRaf = window.requestAnimationFrame(step);
          return;
        }

        const elapsedFrame = Math.max(0, timestamp - state.lastTime);
        state.lastTime = timestamp;
        state.elapsed += elapsedFrame;

        const previousVelocity = state.velocity;
        const decay = Math.exp(-elapsedFrame / MOMENTUM_TIME_CONSTANT);
        const nextVelocity = previousVelocity * decay;
        const previousScrollLeft = this.viewport.scrollLeft;
        this.viewport.scrollLeft += previousVelocity * MOMENTUM_TIME_CONSTANT * (1 - decay);
        const recentered = this.recenterLoopDuringMomentum();
        const moved = recentered || Math.abs(this.viewport.scrollLeft - previousScrollLeft) > 0.05;
        state.velocity = nextVelocity;

        if (
          !moved ||
          Math.abs(nextVelocity) < MOMENTUM_STOP_VELOCITY ||
          state.elapsed >= MOMENTUM_MAX_DURATION
        ) {
          this.stopMomentum({ settle: true });
          return;
        }

        this.momentumRaf = window.requestAnimationFrame(step);
      };

      this.momentumRaf = window.requestAnimationFrame(step);
      return true;
    }

    recenterLoopDuringMomentum() {
      if (!this.loopActive || this.cycleSpan <= 2) {
        return false;
      }

      const { cycle } = this.nearestRenderedItem;

      if (Math.abs(cycle) < LOOP_BUFFER_CYCLES) {
        return false;
      }

      this.viewport.dataset.carouselRecentering = 'true';
      this.viewport.scrollLeft -= cycle * this.cycleSpan;
      window.cancelAnimationFrame(this.momentumRecenterRaf);
      this.momentumRecenterRaf = window.requestAnimationFrame(() => {
        if (!this.isRecentering) {
          delete this.viewport.dataset.carouselRecentering;
        }
      });

      return true;
    }

    stopMomentum({ settle = false } = {}) {
      const wasActive = this.isMomentumActive || Boolean(this.momentumState);
      window.cancelAnimationFrame(this.momentumRaf);
      this.momentumRaf = null;
      this.momentumState = null;
      this.isMomentumActive = false;

      if (this.viewport) {
        delete this.viewport.dataset.momentum;
      }

      if (wasActive && settle) {
        this.queueScrollSettled(80);
      }

      return wasActive;
    }

    handleResize(force = false) {
      const width = this.viewport.clientWidth;

      if (!force && Math.abs(width - this.lastViewportWidth) < 1) {
        return;
      }

      const hadMomentum = this.isMomentumActive;

      if (hadMomentum) {
        this.refreshRenderedPositions();
      }

      const momentumLogicalIndex = hadMomentum ? this.currentIndex : null;
      this.stopMomentum({ settle: false });
      this.lastViewportWidth = width;
      const pendingItem = this.pendingPhysicalIndex === null
        ? null
        : this.renderedItems[this.pendingPhysicalIndex];
      const logicalIndex = pendingItem
        ? Number.parseInt(pendingItem.dataset.carouselLogicalIndex || '0', 10)
        : momentumLogicalIndex ?? this.activeLogicalIndex;
      window.cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = window.requestAnimationFrame(() => {
        if (this.isPointerActive) {
          this.pendingResize = true;
          return;
        }

        this.stopAutoplay();
        this.isScrolling = false;
        this.pendingPhysicalIndex = null;
        this.configureLoop({ logicalIndex });
      });
    }

    updateCounter({ commit = false } = {}) {
      const currentIndex = this.currentIndex;

      if (commit) {
        this.activeLogicalIndex = currentIndex;
      }

      if (this.currentCounter) {
        this.currentCounter.textContent = String(currentIndex + 1);
      }
    }

    updateControls() {
      const { atStart, atEnd } = this.scrollBoundaries;
      const disabledWithoutScroll = !this.hasScrollableContent;

      if (this.previousButton) {
        this.previousButton.disabled = disabledWithoutScroll || (!this.loopRequested && atStart);
      }

      if (this.nextButton) {
        this.nextButton.disabled = disabledWithoutScroll || (!this.loopRequested && atEnd);
      }
    }

    updateState() {
      if (this.navigation) {
        this.navigation.hidden = this.items.length <= 1 || !this.hasScrollableContent;
      }

      this.updateControls();
      this.updateCounter({ commit: true });
      this.updateAutoplayControl();
    }
  }

  customElements.define(ELEMENT_NAME, PrestigeFiberCarousel);
})();
